import { normalizeContractText, type ContractText } from "./contractText";

/**
 * Org-level proposal wording that admins edit in place on the Proposal page
 * (org_settings.proposal_text, carried in the app as userSettings.proposalText).
 *
 * Every key is optional and a MISSING key means "print the built-in text", so
 * an org that never edited anything — and every proposal sent before this
 * existed, whose stored userSettings copy has no proposalText — prints exactly
 * what it always did. Blank is never stored: clearing a field resets it.
 *
 * Sent proposals freeze this with the rest of userSettings (proposalSend puts
 * the whole object in proposals.data), so renaming a label never changes the
 * wording on a /review link a customer already has.
 */

export type ProposalText = {
  project?: string;
  scope?: string;
  whyHeading?: string;
  nextStepsHeading?: string;
  nextStepsBody?: string;
  validityDays?: number;
  /** Standard contract wording overrides — see shared/contractText.ts. */
  contract?: ContractText;
};

export type ProposalLabelKey =
  | "project"
  | "scope"
  | "whyHeading"
  | "nextStepsHeading";

export const PROPOSAL_LABEL_DEFAULTS: Record<ProposalLabelKey, string> = {
  project: "The project",
  scope: "Scope of work",
  whyHeading: "Why Decks Unique",
  nextStepsHeading: "Next steps",
};

/** Labels and headings are one line, capped so a long one cannot overflow
 *  the hanging-label column. */
export const PROPOSAL_LABEL_MAX = 40;

export const DEFAULT_VALIDITY_DAYS = 120;
export const MAX_VALIDITY_DAYS = 999;

const ONES = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
  "sixteen", "seventeen", "eighteen", "nineteen",
];
const TENS = [
  "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty",
  "ninety",
];

/** 1–999 in words, in the style the Next steps paragraph always used
 *  ("one hundred twenty" — no "and", hyphenated tens: "forty-five"). */
export function numberToWords(n: number): string {
  const v = Math.floor(n);
  if (!(v >= 0 && v <= 999)) return String(n);
  if (v < 20) return ONES[v];
  if (v < 100) {
    const t = TENS[Math.floor(v / 10)];
    return v % 10 ? `${t}-${ONES[v % 10]}` : t;
  }
  const rest = v % 100;
  const head = `${ONES[Math.floor(v / 100)]} hundred`;
  return rest ? `${head} ${numberToWords(rest)}` : head;
}

/** The built-in Next steps paragraph, as plain text. The printed default is
 *  still the original JSX (so its markup is unchanged); this string is what
 *  the editor opens with, and what an edit is compared against. */
export function defaultNextStepsBody(days: number): string {
  return (
    `This proposal is valid for ${numberToWords(days)} (${days}) days from ` +
    "the date issued. Pricing and material availability are subject to " +
    "change after this period. If you’d like to move ahead, we’ll " +
    "prepare a contract covering the scope, schedule, and payment terms for " +
    "your review."
  );
}

/** One line, trimmed, capped. */
export function cleanLabel(raw: string): string {
  return String(raw || "")
    .replace(/[\r\n]+/g, " ")
    .slice(0, PROPOSAL_LABEL_MAX);
}

/** Digits only, at most three of them. "" while the field is being typed. */
export function cleanDaysInput(raw: string): string {
  return String(raw || "").replace(/\D+/g, "").slice(0, 3);
}

function validDays(v: unknown): number | undefined {
  const n = typeof v === "string" ? Number(v) : v;
  if (typeof n !== "number" || !Number.isInteger(n)) return undefined;
  if (n < 1 || n > MAX_VALIDITY_DAYS) return undefined;
  return n;
}

/**
 * The stored shape, and nothing else: unknown keys dropped, blank strings
 * dropped (blank = default), days kept only when a whole number 1–999.
 * Used on both read (a hand-edited or future-format row cannot break the
 * page) and write (blank is never stored).
 */
export function normalizeProposalText(raw: unknown): ProposalText {
  const out: ProposalText = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const r = raw as Record<string, unknown>;
  (Object.keys(PROPOSAL_LABEL_DEFAULTS) as ProposalLabelKey[]).forEach((k) => {
    const v = r[k];
    if (typeof v === "string" && v.trim()) out[k] = cleanLabel(v).trim();
  });
  if (typeof r.nextStepsBody === "string" && r.nextStepsBody.trim()) {
    out.nextStepsBody = r.nextStepsBody.replace(/\r\n?/g, "\n").trim();
  }
  const days = validDays(r.validityDays);
  if (days !== undefined) out.validityDays = days;
  const contract = normalizeContractText(r.contract);
  if (Object.keys(contract).length > 0) out.contract = contract;
  return out;
}

export function isProposalTextEmpty(t: ProposalText | null | undefined): boolean {
  return !t || Object.keys(normalizeProposalText(t)).length === 0;
}

/** What prints for a label: the override, else the built-in text. */
export function resolveLabel(
  t: ProposalText | null | undefined,
  key: ProposalLabelKey
): string {
  const v = t?.[key];
  return typeof v === "string" && v.trim() ? v.trim() : PROPOSAL_LABEL_DEFAULTS[key];
}

export function resolveValidityDays(t: ProposalText | null | undefined): number {
  return validDays(t?.validityDays) ?? DEFAULT_VALIDITY_DAYS;
}

/** The custom Next steps body, or null when the built-in one prints. */
export function resolveNextStepsBody(
  t: ProposalText | null | undefined
): string | null {
  const v = t?.nextStepsBody;
  return typeof v === "string" && v.trim() ? v.trim() : null;
}
